import useFormatter from "@/hooks/use-formatter.hook";
import i18n from "@/i18n.ts";
import useEmployeeSearchInline, {
  useEmployeeDelete,
} from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployee, EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Modal,
  ModalProps,
  Select,
  Stack,
  Text,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

export interface EmployeeDeleteModalProps extends ModalProps {
  employee: ApiEmployee;
}

export function EmployeeDeleteModal({ employee, ...props }: EmployeeDeleteModalProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();
  const { showInfo } = useToasts();
  const { renderEmployeeName } = useFormatter();
  const navigate = useNavigate();

  const employeeDelete = useEmployeeDelete();

  const schema = z.object({
    successor_employee_id: z
      .number({ error: i18n.t("errors:field_required") })
      .nullable()
      .refine((value) => value != null, { error: i18n.t("errors:field_required") }),
  });

  const successorSearch = useEmployeeSearchInline(true, {
    ...EmployeeFilterDefault,
    exclude_ids: [employee.id],
  });

  const {
    control,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: { successor_employee_id: null },
  });

  const handleDelete = async () => {
    const validateResult = await trigger();
    if (!validateResult) return;

    const successorEmployeeId = getValues("successor_employee_id");
    if (successorEmployeeId == null) return;

    try {
      await employeeDelete.mutateAsync({
        employeeId: employee.id,
        successorEmployeeId,
      });

      analyticsService.trackEvent(analyticEvents.employees.deleted, {
        [analyticProperties.id]: employee.id,
      });

      showInfo({
        title: t("core:delete_employee_success", { name: renderEmployeeName(employee) }),
      });

      close(id);
      navigate("/employees");
    } catch {
      return;
    }
  };

  return (
    <Modal
      title={t("core:delete_employee")}
      caption={renderEmployeeName(employee)}
      actions={
        <Button
          size={"lg"}
          className={"w-full"}
          onClick={handleDelete}
          isLoading={employeeDelete.isPending}
        >
          {t("common:delete")}
        </Button>
      }
      withCloser={true}
      {...props}
    >
      <Stack gap="lg">
        <Text>{t("core:delete_employee_confirm")}</Text>

        <Controller
          render={({ field: { value, onChange } }) => (
            <Stack gap={"xs"}>
              <Select
                label={t("core:termination_handover_to")}
                required={true}
                emptyTitle={t("common:select")}
                value={value}
                searchable
                isClearable
                showAvatar={true}
                showCaption={true}
                loadOptionsOnOpen
                isLoading={successorSearch.isLoading}
                onSearchOptions={(text) => successorSearch.search(text)}
                searchOptions={successorSearch.options}
                onChange={onChange}
                error={errors.successor_employee_id?.message}
              />
              <Text className={"text-secondary py-2 text-sm"}>
                {t("core:delete_employee_handover_description")}
              </Text>
            </Stack>
          )}
          control={control}
          name={"successor_employee_id"}
        />
      </Stack>
    </Modal>
  );
}
